/**
 * Service for managing document downloads and product information.
 * Handles file downloads, document retrieval, and download status tracking.
 */

import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService, Document } from './api.service';
import { HttpParams } from '@angular/common/http';

/**
 * Service responsible for managing document downloads and product information.
 *
 * @remarks Provides functionality for fetching available documents from the API,
 * downloading files to the user's device, and managing download operation status.
 * Integrates with the API service for backend communication and file-saver for downloads.
 *
 */
@Injectable({
  providedIn: 'root',
})
export class DownloadsService {
  /**
   * Creates an instance of DownloadsService.
   *
   * @param {ApiService} apiService - Service for API communication
   */
  constructor(private apiService: ApiService) {}

  /**
   * Current status of download operations.
   *
   * @remarks Tracks the state of download operations:
   * - idle: No operation in progress
   * - pending: Download operation in progress
   * - success: Last operation completed successfully
   * - error: Last operation failed
   *
   */
  public status: 'idle' | 'pending' | 'success' | 'error' = 'idle';

  /**
   * List of all available documents for download.
   *
   * @remarks Contains the complete list of documents retrieved from the API
   * that are available for download by users.
   *
   */
  public downloads: Document[] = [];

  /**
   * List of product documents.
   *
   * @remarks Contains documents specifically related to products,
   * typically used for product information and specifications.
   *
   */
  public products: Document[] = [];

  /**
   * Fetches all available documents from the API.
   *
   * @remarks Retrieves the complete list of documents available for download
   * from the backend API. Updates the service status and populates the downloads
   * and products arrays with the retrieved data.
   *
   * @returns {Observable<Document[]>} An Observable of Document array containing all available downloads
   */
  public getDownloads(): Observable<Document[]> {
    this.status = 'pending';

    const params = new HttpParams().set('mandant', '66');

    return this.apiService.get<{ documents: Document[] }>('/listDocuments', params).pipe(
      map(data => {
        this.status = 'success';

        this.products = data.documents;
        this.downloads = data.documents;

        return data.documents;
      }),
      catchError(error => {
        this.status = 'error';
        throw error;
      })
    );
  }

  /**
   * Downloads a specific file and saves it to the user's device.
   *
   * @remarks Fetches a document from the API as a blob and automatically
   * triggers a download to the user's device using the file-saver library.
   * The file is saved as a PDF with the specified filename.
   *
   * @param {string} id - The unique identifier of the document to download
   * @param {string} name - The filename to use when saving the document
   * @returns {Observable<any>} An Observable that completes when the download is finished
   */
  public downloadFile(id: string, name: string): Observable<any> {
    const params = new HttpParams().set('id', id).set('name', name);

    return this.apiService.getBlob('/getDocument', params).pipe(
      map(content => {
        saveAs(new Blob([content], { type: 'application/pdf' }), name);
      })
    );
  }

  /**
   * Retrieves the associated document ID for a product.
   *
   * @remarks Searches through a product's attributes to find the 'info_doc_id'
   * attribute value, which contains the ID of the associated information document.
   * Used to link products with their downloadable documentation.
   *
   * @param {Document} product - The product document to extract information from
   * @returns {string | undefined} The value of the 'info_doc_id' attribute or undefined if not found
   */
  public getProductDownloads(product: Document): string | undefined {
    const attribute = product.attributes.find(
      (attribute: { description: string; value: string }) => attribute.description === 'info_doc_id'
    );

    return attribute ? attribute.value : undefined;
  }
}
